import React, { useState, useRef, useEffect } from 'react';
import { Routes, Route } from 'react-router-dom';
import './App.css';
import Login from './components/Login';
import Register from './components/Register';
import ForgotPassword from './components/ForgotPassword';
import ResetPassword from './components/ResetPassword';
import InputForm from './components/InputForm';
import SquareGridChart from './components/SquareGridChart';
import SavedCharts from './components/SavedCharts';
import ErrorMessage from './components/ErrorMessage';
import { fetchHoroscope } from './services/horoscopeApi';
import { isAuthenticated, logout } from './services/authApi';

function App() {
  const [horoscopeData, setHoroscopeData] = useState(null);
  const [chartId, setChartId] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [currentView, setCurrentView] = useState('form'); // 'form' or 'chart'
  const [isLoggedIn, setIsLoggedIn] = useState(isAuthenticated());
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [showRegisterModal, setShowRegisterModal] = useState(false);
  const [showForgotPasswordModal, setShowForgotPasswordModal] = useState(false);
  const chartRef = useRef(null);
  const chartSectionRef = useRef(null);
  const detailsSectionRef = useRef(null);

  

  // Check authentication on mount
  useEffect(() => {
    const isAuth = isAuthenticated();
    console.log('Auth check on mount:', isAuth);
    console.log('Token in storage:', localStorage.getItem('authToken'));
    if (isAuth) {
      setIsLoggedIn(true);
    }
  }, []);

  // Scroll to chart when data is loaded
  useEffect(() => {
    if (horoscopeData && chartRef.current) {
      setTimeout(() => {
        chartRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 100);
    }
  }, [horoscopeData]);

  const handleLoginSuccess = (userData) => {
    console.log('Login successful:', userData);
    setIsLoggedIn(true);
    console.log('isLoggedIn state set to true');
    setShowLoginModal(false);
    setCurrentView('form');
  };

  const handleLogout = async () => {
    await logout();
    setIsLoggedIn(false);
    setCurrentView('form');
    setHoroscopeData(null);
  };

  const handleRegisterSuccess = (userData) => {
    setShowRegisterModal(false);
    setShowLoginModal(true);
    // Show a message that registration was successful
  };

  const handleFormSubmit = async (placename, birthDate, birthTime, latitude, longitude, city, country) => {
    // Users can generate charts WITHOUT login
    // Login is only required to SAVE charts
    setIsLoading(true);
    setError(null);
    try {
      const data = await fetchHoroscope(placename, birthDate, birthTime, latitude, longitude, city, country);
      setHoroscopeData(data);
      // Store the chart ID from response (handle both chart_id and chartId)
      const id = data.chart_id || data.chartId || data.uid;
      setChartId(id);
      console.log('Chart ID stored:', id, 'Full data:', data);
      setCurrentView('chart'); // Switch to chart view
    } catch (err) {
      const errorMessage = err.response?.data?.message || 
                          err.message || 
                          'Failed to fetch horoscope data. Make sure the API server is running at http://localhost:8000';
      setError(errorMessage);
      setHoroscopeData(null);
      setChartId(null);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCloseError = () => {
    setError(null);
  };

  const handleHomeClick = () => {
    setCurrentView('form');
    setHoroscopeData(null);
    setChartId(null);
    setError(null);
  };

  // Scroll to chart section
  const scrollToChart = () => {
    if (chartSectionRef.current) {
      chartSectionRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  // Scroll to details section
  const scrollToDetails = () => {
    if (detailsSectionRef.current) {
      detailsSectionRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  return (
    <>
      <Routes>
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="*" element={
      <div className="app-container">
        {/* Header with Navigation buttons */}
        <header className="app-header">
          <div className="header-content">
            {currentView === 'form' && (
              <div className="home-placeholder"></div>
            )}
            
            <h1 className="app-title">✨ AstroProfile ✨</h1>
            
            {currentView === 'chart' && (
              <div className="header-buttons">
                 <button className="home-btn" onClick={handleHomeClick} title="Return to home">
                🏠 Home
              </button>
                <button className="nav-header-btn" onClick={scrollToChart} title="Scroll to chart">
                  📈 Chart
                </button>
                <button className="nav-header-btn" onClick={scrollToDetails} title="Scroll to details">
                  📋 Details
                </button>
                {isLoggedIn && (
                  <button className="logout-btn" onClick={handleLogout} title="Logout">
                    🚪 Logout
                  </button>
                )}
              </div>
            )}
            {currentView === 'form' && (
              <div className="header-buttons">
                {isLoggedIn && (
                  <button className="logout-btn" onClick={handleLogout} title="Logout">
                    🚪 Logout
                  </button>
                )}
                {!isLoggedIn && (
                  <button className="login-btn" onClick={() => setShowLoginModal(true)} title="Login">
                    🔐 Login
                  </button>
                )}
              </div>
            )}
          </div>
        </header>

        {/* Show Login Modal if needed */}
        {!isLoggedIn && showLoginModal && (
          <div className="login-modal-overlay" onClick={() => setShowLoginModal(false)}>
            <div className="login-modal-content" onClick={(e) => e.stopPropagation()}>
              <button className="modal-close-btn" onClick={() => setShowLoginModal(false)}>✕</button>
              <Login 
                onLoginSuccess={handleLoginSuccess}
                onToggleRegister={() => {
                  setShowLoginModal(false);
                  setShowRegisterModal(true);
                }}
                onForgotPassword={() => {
                  setShowLoginModal(false);
                  setShowForgotPasswordModal(true);
                }}
              />
            </div>
          </div>
        )}

        {/* Show Register Modal if needed */}
        {!isLoggedIn && showRegisterModal && (
          <div className="login-modal-overlay" onClick={() => setShowRegisterModal(false)}>
            <div className="login-modal-content" onClick={(e) => e.stopPropagation()}>
              <button className="modal-close-btn" onClick={() => setShowRegisterModal(false)}>✕</button>
              <Register 
                onRegisterSuccess={handleRegisterSuccess}
                onToggleForm={() => {
                  setShowRegisterModal(false);
                  setShowLoginModal(true);
                }}
              />
            </div>
          </div>
        )}

        {/* Show Forgot Password Modal if needed */}
        {!isLoggedIn && showForgotPasswordModal && (
          <div className="login-modal-overlay" onClick={() => setShowForgotPasswordModal(false)}>
            <div className="login-modal-content" onClick={(e) => e.stopPropagation()}>
              <ForgotPassword 
                onBack={() => {
                  setShowForgotPasswordModal(false);
                  setShowLoginModal(true);
                }}
                onResetSuccess={() => {
                  setShowForgotPasswordModal(false);
                  setShowLoginModal(true);
                }}
              />
            </div>
          </div>
        )}

        {/* Form View */}
        {currentView === 'form' && (
          <div className="view-container form-view">
            <InputForm onSubmit={handleFormSubmit} isLoading={isLoading} />
            {isLoggedIn && <SavedCharts onChartSelected={(chartData) => {
              setError(null);  // Clear any previous error message
              setHoroscopeData(chartData);
              // Handle both chart_id and chartId property names
              setChartId(chartData.chart_id || chartData.chartId || null);
              setCurrentView('chart');
              setTimeout(() => {
                if (chartRef.current) {
                  chartRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
                }
              }, 100);
            }} />}
          </div>
        )}

        {/* Chart View */}
        {currentView === 'chart' && (
          <div className="view-container chart-view">
            {error && <ErrorMessage message={error} onClose={handleCloseError} />}
            {horoscopeData && <div ref={chartRef}><SquareGridChart isLoggedIn={isLoggedIn} onLoginRequired={() => setShowLoginModal(true)} horoscopeData={horoscopeData} chartSectionRef={chartSectionRef} detailsSectionRef={detailsSectionRef} chartId={chartId} /></div>}
          </div>
        )}
      </div>
        } />
      </Routes>
    </>
  );
}

export default App;